import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Filter, Image as ImageIcon, Plus, Search, Trash2, Upload, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';

type BodyChartTemplate = {
  id: string;
  name: string;
  type: 'CHART' | 'STENCIL';
  labels: string[];
  fileName: string | null;
  mimeType: string | null;
  assetUrl: string;
  active: boolean;
  updatedAt: string;
};

type Draft = {
  name: string;
  type: 'CHART' | 'STENCIL';
  labelsText: string;
  fileName: string;
  mimeType: string;
  assetUrl: string;
};

const emptyDraft = (): Draft => ({
  name: '',
  type: 'CHART',
  labelsText: '',
  fileName: '',
  mimeType: '',
  assetUrl: '',
});

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Unable to read file.'));
    reader.readAsDataURL(file);
  });
}

export default function SettingsCharts() {
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { tr } = useI18n();
  const [charts, setCharts] = useState<BodyChartTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'CHART' | 'STENCIL'>('ALL');
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const loadCharts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'ALL') params.set('type', typeFilter);
      const rows = await fetchJson<BodyChartTemplate[]>(`/body-charts?${params.toString()}`);
      setCharts(rows);
    } catch (err: any) {
      setError(err?.message || tr('Unable to load charts.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, tr, typeFilter]);

  useEffect(() => {
    void loadCharts();
  }, [loadCharts]);

  const filteredCharts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return charts;
    return charts.filter((chart) =>
      [chart.name, chart.type, chart.fileName || '', ...chart.labels]
        .some((value) => value.toLowerCase().includes(q)),
    );
  }, [charts, query]);

  async function handleFile(file: File | null) {
    if (!file) return;
    setError(null);
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError(tr('Upload an image or PDF chart template.'));
      return;
    }
    const assetUrl = await fileToDataUrl(file);
    setDraft((prev) => ({
      ...prev,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      assetUrl,
      name: prev.name || file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
    }));
  }

  async function saveChart() {
    if (!draft.name.trim() || !draft.assetUrl) {
      setError(tr('Name and uploaded file are required.'));
      return;
    }
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      await fetchJson<BodyChartTemplate>('/body-charts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: draft.name.trim(),
          type: draft.type,
          labels: draft.labelsText.split(',').map((label) => label.trim()).filter(Boolean),
          fileName: draft.fileName || undefined,
          mimeType: draft.mimeType || undefined,
          assetUrl: draft.assetUrl,
          thumbnailUrl: draft.assetUrl,
          active: true,
        }),
      });
      setBanner(tr('Chart saved.'));
      setModalOpen(false);
      setDraft(emptyDraft());
      await loadCharts();
    } catch (err: any) {
      setError(err?.message || tr('Failed to save chart.'));
    } finally {
      setSaving(false);
    }
  }

  async function archiveChart(chart: BodyChartTemplate) {
    if (!window.confirm(tr('Archive this chart template?'))) return;
    setError(null);
    try {
      const response = await fetchApi(`/body-charts/${chart.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      setBanner(tr('Chart archived.'));
      await loadCharts();
    } catch (err: any) {
      setError(err?.message || tr('Failed to archive chart.'));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/settings')} className="rounded-full p-2 hover:bg-slate-100">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{tr('Charts')}</h1>
            <p className="text-sm text-slate-500">
              {tr('Upload reusable treatment-area charts and transparent stencils for clinical documentation.')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setDraft(emptyDraft());
            setModalOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white"
        >
          <Plus size={16} />
          {tr('Add Chart')}
        </button>
      </div>

      <section className="rounded-lg border border-cyan-100 bg-cyan-50 px-5 py-4">
        <h2 className="text-sm font-semibold text-slate-900">{tr('Body Charts')}</h2>
        <p className="mt-1 max-w-3xl text-xs text-slate-600">
          {tr('Charts document treatment areas in forms. Stencils are transparent overlays that help staff position client photos consistently.')}
        </p>
      </section>

      {banner ? <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{banner}</div> : null}
      {error ? <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-[260px] flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={tr('Search by name or label')}
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          {(['ALL', 'CHART', 'STENCIL'] as const).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTypeFilter(item)}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold ${typeFilter === item ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              {item === 'ALL' ? <Filter size={13} /> : null}
              {item === 'ALL' ? tr('All') : item === 'CHART' ? tr('Charts') : tr('Stencils')}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">{tr('Loading charts...')}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {filteredCharts.map((chart) => (
            <article key={chart.id} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded border border-slate-100 bg-slate-50">
                {chart.mimeType === 'application/pdf' ? (
                  <div className="text-center text-sm text-slate-500">
                    <ImageIcon className="mx-auto mb-2" size={28} />
                    {chart.fileName || 'PDF'}
                  </div>
                ) : (
                  <img src={chart.assetUrl} alt={chart.name} className="h-full w-full object-contain" />
                )}
              </div>
              <div className="mt-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-slate-900">{chart.name}</h3>
                  <p className="text-xs font-medium text-cyan-700">{chart.type === 'STENCIL' ? tr('STENCIL') : tr('CHART')}</p>
                </div>
                <button type="button" onClick={() => archiveChart(chart)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 size={15} />
                </button>
              </div>
              {chart.labels.length ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {chart.labels.slice(0, 4).map((label) => (
                    <span key={`${chart.id}-${label}`} className="rounded bg-cyan-50 px-2 py-1 text-[11px] font-medium text-cyan-700">
                      {label}
                    </span>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
          {!filteredCharts.length ? (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500 md:col-span-2 xl:col-span-4">
              {tr('No charts found.')}
            </div>
          ) : null}
        </div>
      )}

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">{tr('Upload New Template')}</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded p-1 text-slate-400 hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <label className="flex min-h-[140px] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center hover:bg-slate-100">
                <Upload size={22} className="text-slate-500" />
                <span className="mt-2 text-sm font-semibold text-slate-700">{draft.fileName || tr('Drop Files Here To Upload')}</span>
                <span className="mt-1 text-xs text-slate-500">{tr('Choose file')}</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(event) => void handleFile(event.target.files?.[0] || null)} />
              </label>

              <label className="block text-sm font-medium text-slate-700">
                {tr('Name')}
                <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={draft.name} onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))} />
              </label>

              <div className="space-y-2 text-sm font-medium text-slate-700">
                {tr('Type')}
                <div className="flex gap-4 rounded-lg border border-slate-200 px-3 py-2">
                  <label className="inline-flex items-center gap-2 text-sm font-normal">
                    <input type="radio" checked={draft.type === 'CHART'} onChange={() => setDraft((prev) => ({ ...prev, type: 'CHART' }))} />
                    {tr('Chart')}
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm font-normal">
                    <input type="radio" checked={draft.type === 'STENCIL'} onChange={() => setDraft((prev) => ({ ...prev, type: 'STENCIL' }))} />
                    {tr('Stencil')}
                  </label>
                </div>
              </div>

              <label className="block text-sm font-medium text-slate-700">
                {tr('Label')}
                <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={draft.labelsText} onChange={(event) => setDraft((prev) => ({ ...prev, labelsText: event.target.value }))} />
                <span className="mt-1 block text-xs font-normal text-slate-500">{tr('For multiple labels use comma separated values')}</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">
                {tr('Cancel')}
              </button>
              <button type="button" disabled={saving} onClick={saveChart} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {saving ? tr('Saving...') : tr('Save')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
